/**
 * @fileoverview Password-reset request page. Shared by all five portals
 * (instructor, student, parent, organization admin and Hans Ops, see
 * `PORTALS`) via the `portal` prop; on submit it always shows a generic
 * "check your email" confirmation so the page never reveals whether an email
 * is registered.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";

const PORTALS = {
  instructor: {
    subtitle: "Instructor Portal",
    endpoint: "/auth/forgot-password",
    loginPath: "/login",
  },
  student: {
    subtitle: "Student Portal",
    endpoint: "/student/auth/forgot-password",
    loginPath: "/student/login",
  },
  organization: {
    subtitle: "Organization Admin",
    endpoint: "/org/auth/forgot-password",
    loginPath: "/org/login",
  },
  parent: {
    subtitle: "Parent Portal",
    endpoint: "/parent/auth/forgot-password",
    loginPath: "/parent/login",
  },
  platform: {
    subtitle: "Hans Ops",
    endpoint: "/ops/auth/forgot-password",
    loginPath: "/ops/login",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordPage = ({ portal = "instructor" }) => {
  const config = PORTALS[portal] || PORTALS.instructor;
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError("Enter a valid email address");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}${config.endpoint}`, { email: email.trim() }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      toast.error(getErrorMessage(err, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 studio-spotlight">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
            <span className="text-lg font-semibold text-[var(--text-primary)] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hans
            </span>
          </div>
          <h1 className="text-2xl font-light text-[var(--text-primary)] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Reset your password
          </h1>
          <p className="text-[var(--text-secondary)] text-sm">{config.subtitle}</p>
        </div>

        {sent ? (
          <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 text-center space-y-3" data-testid="reset-sent">
            <p className="text-[var(--text-primary)] text-sm">
              If an account exists for <span className="font-medium">{email.trim()}</span>, we've sent a link to reset
              your password.
            </p>
            <p className="text-[var(--text-tertiary)] text-xs">
              The link expires in one hour. Check your spam folder if it doesn't arrive shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-6 space-y-4">
              <p className="text-[var(--text-secondary)] text-sm">
                Enter the email associated with your account and we'll send you a link to choose a new password.
              </p>
              <div>
                <Label className="text-[var(--text-tertiary)] text-xs uppercase tracking-wider">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                  required
                  aria-invalid={error ? "true" : undefined}
                  className={`mt-1 bg-[var(--studio-base)] text-[var(--text-primary)] ${error ? "border-[var(--state-danger)]" : "border-[var(--studio-divider-strong)]"}`}
                  placeholder="your@email.com"
                  autoComplete="email"
                />
                {error && (
                  <p className="text-[var(--state-danger)] text-xs mt-1" data-testid="email-error">{error}</p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--accent-yellow)] text-[var(--text-inverse)] font-medium rounded-full py-3
                         hover:scale-105 active:scale-95 transition-transform text-sm disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Send reset link
            </Button>
          </form>
        )}

        <div className="text-center mt-6">
          <Link to={config.loginPath} className="text-[var(--text-tertiary)] text-xs hover:text-[var(--text-primary)] transition-colors inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
